// Seasonal residential pricing: a summer rate and cadence and a winter
// rate and cadence on one quote (first use: deal 203081208, twice weekly
// at $390 May through September, weekly at $215 October through April).
//
// Operator-set on the stored quote, like specialtyPriceOverrides and
// visitsPerWeek, so a Sell republish never clears it. When present it is
// authoritative: the season in effect on a given day decides the monthly
// price and cadence the page shows, the card authorization covers, and the
// QBO recurring template starts at. The other season's switch is manual
// (billing edits QBO and the route), flagged on the signing ticket and
// Sell note; reminders for each switch are set on the deal by hand.

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

const validMonth = (value) => Number.isInteger(value) && value >= 1 && value <= 12;

const validPrice = (value) => typeof value === 'number' && Number.isFinite(value) && value > 0;

// Returns the normalized object, or null when the value is absent or
// malformed. Callers that must reject bad input (the publish endpoint)
// compare against the raw value themselves.
export function normalizeSeasonalPricing(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const season = (s) => {
    if (!s || typeof s !== 'object' || Array.isArray(s)) return null;
    if (!validPrice(s.monthlyPrice) || !validMonth(s.startMonth)) return null;
    if (![0.5, 1, 2].includes(s.visitsPerWeek)) return null;
    return {
      monthlyPrice: Math.round(s.monthlyPrice * 100) / 100,
      visitsPerWeek: s.visitsPerWeek,
      startMonth: s.startMonth,
    };
  };
  const summer = season(raw.summer);
  const winter = season(raw.winter);
  if (!summer || !winter || summer.startMonth === winter.startMonth) return null;
  if (raw.startWindow !== undefined && (typeof raw.startWindow !== 'string' || !raw.startWindow.trim() || raw.startWindow.length > 100)) return null;
  if (raw.startMonth !== undefined && !/^20\d{2}-(0[1-9]|1[0-2])$/.test(raw.startMonth)) return null;
  return { summer, winter,
    ...(raw.startWindow ? { startWindow: raw.startWindow.trim() } : {}),
    ...(raw.startMonth ? { startMonth: raw.startMonth } : {}),
  };
}

// Calendar month (1-12) in Pacific time, the timezone billing runs on.
export function pacificMonth(date = new Date()) {
  return Number(new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles', month: 'numeric',
  }).format(date));
}

const inSeason = (month, start, end) => (start <= end
  ? month >= start && month <= end
  : month >= start || month <= end);

const lastMonthBefore = (month) => (month === 1 ? 12 : month - 1);

export function activeSeasonName(seasonal, date = new Date()) {
  const calendar = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit' }).formatToParts(date);
  const currentMonth = `${calendar.find(p => p.type === 'year').value}-${calendar.find(p => p.type === 'month').value}`;
  const month = seasonal.startMonth && currentMonth < seasonal.startMonth
    ? Number(seasonal.startMonth.slice(5)) : pacificMonth(date);
  return inSeason(month, seasonal.summer.startMonth, lastMonthBefore(seasonal.winter.startMonth))
    ? 'summer'
    : 'winter';
}

// "May through September"
export function seasonMonthsLabel(seasonal, name) {
  const other = name === 'summer' ? 'winter' : 'summer';
  return `${MONTH_NAMES[seasonal[name].startMonth - 1]} through ${MONTH_NAMES[lastMonthBefore(seasonal[other].startMonth) - 1]}`;
}

export const cadenceText = (visitsPerWeek) => (visitsPerWeek === 0.5 ? 'every other week' : visitsPerWeek === 2 ? 'twice weekly' : 'weekly');

const money = (n) => (Number.isInteger(n) ? String(n) : n.toFixed(2));

// One line per season, summer first: used on the page, the agreement, the
// ticket, and the Sell note so every surface states the same terms.
export function seasonalSummaryLines(seasonal) {
  return ['summer', 'winter'].map((name) => {
    const s = seasonal[name];
    const label = name === 'summer' ? 'Summer' : 'Winter';
    const perVisit = seasonal.summer.visitsPerWeek === 0.5 || seasonal.winter.visitsPerWeek === 0.5;
    return `${label} (${seasonMonthsLabel(seasonal, name)}): ${cadenceText(s.visitsPerWeek)} service, ${perVisit ? `$${money(s.monthlyPrice / (4 * s.visitsPerWeek))}/visit; ` : ''}$${money(s.monthlyPrice)}/month${perVisit ? ` for ${4 * s.visitsPerWeek} visits` : ''}`;
  });
}

// The quote as it applies on `date`: the active season's monthly price,
// weekly display price, and cadence replace the published single-rate
// fields. Quotes without seasonal pricing are returned unchanged.
export function withActiveSeason(quote, date = new Date()) {
  const seasonal = normalizeSeasonalPricing(quote?.seasonalPricing);
  if (!seasonal) return quote;
  const name = activeSeasonName(seasonal, date);
  const season = seasonal[name];
  return {
    ...quote,
    seasonalPricing: seasonal,
    activeSeason: name,
    monthlyPrice: season.monthlyPrice,
    weeklyPrice: Math.round(season.monthlyPrice / 4),
    visitsPerWeek: season.visitsPerWeek,
  };
}

// First day of the next season change on or after `date`, as YYYY-MM-DD
// in Pacific time, with the season that starts then.
export function nextSeasonSwitch(seasonal, date = new Date()) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles', year: 'numeric', month: 'numeric',
  }).formatToParts(date).reduce((acc, p) => ({ ...acc, [p.type]: Number(p.value) }), {});
  if (seasonal.startMonth && `${parts.year}-${String(parts.month).padStart(2, '0')}` < seasonal.startMonth) {
    parts.year = Number(seasonal.startMonth.slice(0, 4));
    parts.month = Number(seasonal.startMonth.slice(5));
  }
  const upcoming = activeSeasonName(seasonal, date) === 'summer' ? 'winter' : 'summer';
  const startMonth = seasonal[upcoming].startMonth;
  const year = startMonth > parts.month ? parts.year : parts.year + 1;
  return { season: upcoming, date: `${year}-${String(startMonth).padStart(2, '0')}-01` };
}
